import type { LaunchPlanDocument } from './api';

export type StoredLaunchPlan = {
  id: string;
  document: LaunchPlanDocument;
  pending: boolean;
  serverExists: boolean;
  baseData?: Record<string, unknown>;
};

const databaseName = 'angelmyth-launch-plans-v1';
const storeName = 'plans';
const fallbackKey = 'angelmyth-launch-plans-fallback-v1';

export async function readStoredLaunchPlans(): Promise<StoredLaunchPlan[]> {
  if (!('indexedDB' in globalThis)) return readFallback();
  try {
    const database = await openDatabase();
    return await new Promise((resolve, reject) => {
      const request = database.transaction(storeName, 'readonly').objectStore(storeName).getAll();
      request.onsuccess = () => resolve((request.result ?? []) as StoredLaunchPlan[]);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.warn('Unable to read launch-plan IndexedDB cache', error);
    return readFallback();
  }
}

export async function writeStoredLaunchPlan(record: StoredLaunchPlan): Promise<void> {
  if (!('indexedDB' in globalThis)) return writeFallback(record);
  try {
    const database = await openDatabase();
    await new Promise<void>((resolve, reject) => {
      const request = database.transaction(storeName, 'readwrite').objectStore(storeName).put(record);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.warn('Unable to write launch-plan IndexedDB cache', error);
    writeFallback(record);
  }
}

export async function removeStoredLaunchPlan(id: string): Promise<void> {
  if (!('indexedDB' in globalThis)) return removeFallback(id);
  try {
    const database = await openDatabase();
    await new Promise<void>((resolve, reject) => {
      const request = database.transaction(storeName, 'readwrite').objectStore(storeName).delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.warn('Unable to remove launch-plan IndexedDB cache', error);
    removeFallback(id);
  }
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(databaseName, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(storeName)) request.result.createObjectStore(storeName, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function readFallback(): StoredLaunchPlan[] {
  try { return JSON.parse(localStorage.getItem(fallbackKey) || '[]') as StoredLaunchPlan[]; }
  catch { return []; }
}

function writeFallback(record: StoredLaunchPlan): void {
  const records = readFallback().filter(item => item.id !== record.id);
  records.push(record);
  try { localStorage.setItem(fallbackKey, JSON.stringify(records)); }
  catch (error) {
    console.warn('Unable to write launch-plan localStorage fallback', error);
    throw new Error('本机存储空间不足，计划尚未保存，请清理浏览器空间后重试。', { cause:error });
  }
}

function removeFallback(id: string): void {
  try { localStorage.setItem(fallbackKey, JSON.stringify(readFallback().filter(item => item.id !== id))); }
  catch (error) { console.warn('Unable to remove launch-plan localStorage fallback', error); }
}
